import { useEffect, useState } from "react";
import { Link2, Copy, Trash2, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

export function encodeFavorites(items) {
  const compact = items.map((i) => ({ k: i.kind, r: i.ref, n: i.name, m: i.meta || {} }));
  const bytes = new TextEncoder().encode(JSON.stringify(compact));
  let bin = "";
  bytes.forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const isScalar = (v) => v === null || ["string", "number", "boolean"].includes(typeof v);

function cleanMeta(m) {
  if (!m || typeof m !== "object") return {};
  return Object.fromEntries(
    Object.entries(m).filter(([k, v]) => k.length <= 50 && isScalar(v) && !(typeof v === "string" && v.length > 200)).slice(0, 10)
  );
}

export function decodeFavorites(data) {
  const bin = atob(data.replace(/-/g, "+").replace(/_/g, "/"));
  const list = JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0))));
  return list
    .filter((i) => (i.k === "catalog" || i.k === "trending") && typeof i.r === "string" && i.r.length > 0 && i.r.length <= 200)
    .slice(0, 200)
    .map((i) => ({ kind: i.k, ref: i.r, name: String(i.n || i.r).slice(0, 200), meta: cleanMeta(i.m) }));
}

const copy = (url) => navigator.clipboard.writeText(url).then(() => toast.success("Lien copié"), () => toast.error("Copie impossible"));

function SavedLink() {
  const [token, setToken] = useState(undefined);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/favorites/share").then(({ data }) => setToken(data.token)).catch(() => setToken(null)); }, []);
  const url = token ? `${window.location.origin}/favoris/partage/${token}` : "";

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch {
      toast.error("Action impossible, réessayez");
    } finally {
      setBusy(false);
    }
  };
  const create = () => run(async () => {
    const { data } = await api.post("/favorites/share");
    setToken(data.token);
    copy(`${window.location.origin}/favoris/partage/${data.token}`);
  });
  const revoke = () => run(async () => {
    await api.delete("/favorites/share");
    setToken(null);
    toast("Lien désactivé : il ne fonctionne plus");
  });

  if (token === undefined) return null;
  return (
    <div className="space-y-2 border-t border-[#1E2638] pt-3" data-testid="share-saved-section">
      <div className="text-xs font-medium text-slate-200">Lien enregistré (toujours à jour, désactivable)</div>
      {token ? (
        <div className="flex flex-wrap items-center gap-2">
          <code data-testid="share-saved-url" className="min-w-0 flex-1 truncate rounded border border-[#1E2638] bg-[#080B10] px-2 py-1.5 font-mono2 text-[11px] text-slate-300">{url}</code>
          <Button size="sm" variant="outline" onClick={() => copy(url)} data-testid="share-saved-copy-button" className="gap-1"><Copy className="h-3.5 w-3.5" /> Copier</Button>
          <Button size="sm" variant="outline" onClick={revoke} disabled={busy} data-testid="share-saved-revoke-button" className="gap-1 text-red-400"><Trash2 className="h-3.5 w-3.5" /> Désactiver</Button>
        </div>
      ) : (
        <Button size="sm" onClick={create} disabled={busy} data-testid="share-saved-create-button" className="gap-1.5">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Créer un lien enregistré
        </Button>
      )}
    </div>
  );
}

export function ShareFavorites({ items }) {
  const { user } = useAuth();
  const quickUrl = `${window.location.origin}/favoris/partage?d=${encodeFavorites(items)}`;
  return (
    <div className="mt-5 space-y-3 rounded-xl border border-[#1E2638] bg-[#0F141C] p-4" data-testid="share-favorites">
      <div className="flex items-center gap-2 text-sm font-medium"><Share2 className="h-4 w-4 text-primary" /> Partager ma liste</div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => copy(quickUrl)} data-testid="share-quick-copy-button" className="gap-1.5"><Copy className="h-3.5 w-3.5" /> Copier le lien rapide</Button>
        <span className="text-xs text-muted-foreground">Le lien contient la liste actuelle, rien n'est enregistré sur le serveur (non désactivable).</span>
      </div>
      {user ? <SavedLink /> : <p className="text-xs text-muted-foreground">Connectez-vous pour créer un lien enregistré, toujours à jour et désactivable.</p>}
    </div>
  );
}
